/**
 * @file QualityOpsReport.tsx
 * @brief Super Admin report — failed/stuck Meeting Intel + cost alert status.
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Coins, Timer, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import {
  formatDateTime,
  formatMoneyMinor,
} from "@/shared/utils/formatters";
import { useQualityOpsReport } from "../api";
import { ReportShell } from "./ReportShell";
import { ReportTable } from "./ReportTable";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day",
  };
}

export function QualityOpsReportView() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());
  const [companyId, setCompanyId] = useState<string | null>(null);
  const queryRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      companyId: companyId ?? undefined,
    }),
    [range.startDate, range.endDate, companyId],
  );
  const report = useQualityOpsReport(queryRange);
  const summary = report.data?.summary;
  const cost = report.data?.cost_status;
  const meetings = report.data?.meetings ?? [];

  return (
    <ReportShell
      title="Quality / ops"
      description="Meeting Intel errors and stuck pipelines (>2h), plus today's cost-alert status."
      range={range}
      onRangeChange={setRange}
      showCompanyFilter
      companyId={companyId}
      onCompanyChange={setCompanyId}
      exportDisabled={meetings.length === 0}
      serverExport={{
        scope: "admin",
        reportKey: "quality-ops",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
          companyId: companyId ?? undefined,
        },
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-3">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Errors"
              icon={TriangleAlert}
              value={String(summary?.meetings_error ?? 0)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Stuck"
              icon={Timer}
              value={String(summary?.meetings_stuck ?? 0)}
              hint="Processing > 2 hours"
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Today's AI cost"
              icon={Coins}
              value={formatMoneyMinor(cost?.current_cost, cost?.currency ?? "USD")}
              hint={
                cost
                  ? `${Math.round(Number(cost.percent_used || 0))}% of threshold`
                  : undefined
              }
              tone={
                cost?.is_critical
                  ? "warning"
                  : cost?.is_warning
                    ? "warning"
                    : undefined
              }
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Meeting Intel issues"
          description="Open Meeting Intel to reprocess or regenerate."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Issue",
              "Meeting",
              "Company / owner",
              { label: "Updated", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={meetings.length === 0}
            emptyMessage="No quality issues in this range."
          >
            {meetings.map((row) => (
              <tr key={row.id} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <Badge
                    variant={
                      row.issue === "error" ? "destructive" : "warning"
                    }
                  >
                    {row.issue === "error" ? "Error" : "Stuck"}
                  </Badge>
                  <div className="mt-1 text-xs capitalize text-muted-foreground">
                    {row.status}
                  </div>
                </td>
                <td className="px-4 py-2 font-medium">
                  {row.title || "Untitled"}
                </td>
                <td className="px-4 py-2">
                  <div>{row.company_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.owner_email}
                  </div>
                </td>
                <td className="px-4 py-2 text-right text-muted-foreground">
                  {formatDateTime(row.updated_at)}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
